/*
 * sticky-atc.js — "product-conversion" cluster
 *
 * Local replacement for two custom elements that used to be supplied by the
 * removed remote vendor bundle (the vendor runtime):
 *
 *   <sticky-atc>          sections/main-product.liquid:453 - :694  (block `sticky_atc`, :445)
 *   <clickable-discount>  sections/main-product.liquid:193 - :237  (block `clickable_discount`, :190)
 *
 * These two tags are the only ones defined here. The sticky bar's subtree also
 * renders <secondary-variant-select> / <secondary-variant-select-separate>
 * (sections/main-product.liquid:522 open / :628 close) — those belong to the
 * variant-picker cluster and are deliberately NOT defined in this file.
 *
 * Everything below is derived from the theme's own Liquid + assets/base.css.
 * No markup is changed; this file only drives attributes/inline styles that the
 * shipped CSS already reacts to.
 *
 * Wrapped in an IIFE so nothing leaks into the global lexical scope that
 * assets/secondary.js (a classic, non-module script) already occupies.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Small local helpers.
   *
   * assets/secondary.js CALLS `debounce`/`trapFocus`/`removeTrapFocus` but
   * never defines them, so there is nothing to reuse here. These are given
   * deliberately non-colliding, file-scoped names so that whichever cluster
   * ends up introducing the shared globals cannot clash with them.
   * ------------------------------------------------------------------ */

  /** Trailing-edge debounce. */
  function saDebounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /**
   * Wraps a listener so a throw inside it can never bubble out and break an
   * unrelated handler on the same target — most importantly the add-to-cart
   * click binding installed at layout/theme.liquid:702-709.
   */
  function saSafe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        console.error('[sticky-atc]', error);
      }
    };
  }

  /**
   * Locale-aware storefront root, e.g. "/" or "/fr-ca/".
   * window.routes (layout/theme.liquid:672-679) has no root_url key, so this
   * falls back to deriving it from cart_url.
   */
  function saShopRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
    } catch (error) {
      /* Shopify global may be absent in the theme editor preview frame. */
    }
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var root = cartUrl.replace(/cart\/?$/, '');
    if (!root) root = '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  /* ================================================================== *
   * <sticky-atc>
   * ================================================================== *
   *
   * Responsibilities (and only these):
   *
   *  1. Show / hide the bar when it opts into `data-after-scroll="true"`
   *     (sections/main-product.liquid:457-459). assets/base.css ships exactly
   *     one visibility rule:
   *
   *       .sticky-atc[data-after-scroll="true"] {
   *         transform: translateY(100%);
   *         transition: transform 0.15s ease-in-out;
   *       }
   *
   *     There is no companion "shown" class, and the transition lives in the
   *     same rule as the transform — so the attribute must stay put and the
   *     reveal has to be an INLINE transform that overrides it. Removing the
   *     attribute would also remove the animation.
   *
   *  2. Keep `--sticky-atc-offset` up to date on every `.floating-btn`, so the
   *     scroll-to-top button, the music player and the bottom progress bar sit
   *     above the bar instead of underneath it. Those three consume the custom
   *     property in base.css and all carry `.floating-btn`
   *     (sections/scroll-to-top-btn.liquid:16, sections/global-music-player.liquid:15,
   *     sections/page-scroll-progress.liquid:2).
   *
   *  3. In scroll mode (`data-scroll-btn="true"`, :460-461) smooth-scroll to
   *     `data-scroll-destination` (:462).
   *
   * Explicit NON-responsibilities:
   *
   *  - Add to cart. layout/theme.liquid:702-709 already binds every
   *    `[id^="SectionAtcBtn-"]` click to `product-form[data-main="true"]`
   *    .handleSubmit(). Binding here too would add the product twice.
   *  - Variant-change repainting of the image / price / sold-out label. Those
   *    hooks are shared with snippets/buy-buttons.liquid (`.main-atc-price-span-…`
   *    at :130 and :146) and are owned by product-info / variant-selects.
   *  - `.sticky-atc__error` (:653). Written by the custom-product-field
   *    validation path — snippets/custom-product-field.liquid:25 carries
   *    `data-apply-sticky-atc-error`.
   *  - Layout height. layout/theme.liquid:713-714 reads `clientHeight` at parse
   *    time to size the load-animation observer, so the host is never hidden
   *    with `display`; `transform` does not affect the layout box.
   */
  var StickyAtc = class StickyAtcElement extends HTMLElement {
    constructor() {
      super();
      this.shown = false;
      this.observer = null;
      this.scrollHandler = null;
      this.resizeHandler = null;
      this.editorForcedOpen = false;
    }

    connectedCallback() {
      // Re-entrancy guard. `initialised` is cleared again on disconnect so the
      // element re-arms correctly if the theme editor moves the block.
      if (this.initialised) return;
      this.initialised = true;

      try {
        this.sectionId = this.dataset.section || '';
        // `data-after-scroll` is only emitted for display_when == 'after_scroll'
        // (sections/main-product.liquid:457-459). Absent means "always visible",
        // in which case base.css applies no transform and we must not add one.
        this.afterScroll = this.getAttribute('data-after-scroll') === 'true';

        this.setupScrollButton();
        this.setupResize();

        if (this.afterScroll) {
          this.setShown(false, true);
          this.setupRevealTrigger();
        } else {
          // Always-on bar: it is visible from first paint.
          this.setShown(true, true);
        }

        this.setupThemeEditor();
      } catch (error) {
        console.error('[sticky-atc] init failed', error);
      }
    }

    disconnectedCallback() {
      // assets/quick-add.js:88-89 removes this element from its cloned product
      // subtree, so teardown has to be clean and must reset the offset it wrote.
      try {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
          this.scrollHandler = null;
        }
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
          this.resizeHandler = null;
        }
        // Only give the offset back if this instance was the one reserving it.
        if (this.shown) this.writeFloatingOffset(0);
        this.shown = false;
        this.initialised = false;
      } catch (error) {
        console.error('[sticky-atc] teardown failed', error);
      }
    }

    /* ---------------------------------------------------------------- *
     * Visibility
     * ---------------------------------------------------------------- */

    /**
     * @param {boolean} shown
     * @param {boolean} [force] apply even when the state has not changed
     */
    setShown(shown, force) {
      if (this.editorForcedOpen && !shown) return;
      if (!force && shown === this.shown) return;
      this.shown = shown;

      if (this.afterScroll) {
        // Inline transform beats the stylesheet's translateY(100%) while
        // leaving `data-after-scroll` — and therefore the transition — intact.
        if (shown) {
          this.style.transform = 'translateY(0)';
        } else {
          this.style.removeProperty('transform');
        }
      }

      this.updateFloatingOffset();
    }

    /* ---------------------------------------------------------------- *
     * Reveal trigger
     * ---------------------------------------------------------------- */

    /**
     * The schema label is "After scrolling past regular ATC button"
     * (sections/main-product.liquid:4803-4804), so the trigger is the main
     * product submit button, and the bar hides again when it scrolls back in.
     *
     * The Liquid never names the element, so candidates are resolved in
     * most-specific-first order.
     */
    getTriggerElement() {
      var candidates = [];
      if (this.sectionId) {
        // snippets/buy-buttons.liquid:112, rendered with section_id: section.id
        // (sections/main-product.liquid:761).
        candidates.push('#ProductSubmitButton-' + this.sectionId);
      }
      candidates.push('product-form[data-main="true"]'); // snippets/buy-buttons.liquid:58-63

      for (var i = 0; i < candidates.length; i++) {
        var el = null;
        if (candidates[i].charAt(0) === '#') {
          el = document.getElementById(candidates[i].slice(1));
        } else {
          el = document.querySelector(candidates[i]);
        }
        // Never latch onto something inside the sticky bar itself — the sticky
        // button also carries `.main-product-atc` (sections/main-product.liquid:646).
        if (el && !this.contains(el)) return el;
      }

      // `.main-product-atc` is the last resort because of that same overlap.
      var shared = document.querySelectorAll('.main-product-atc');
      for (var j = 0; j < shared.length; j++) {
        if (!this.contains(shared[j])) return shared[j];
      }
      return null;
    }

    setupRevealTrigger() {
      var trigger = this.getTriggerElement();

      if (trigger && 'IntersectionObserver' in window) {
        var self = this;
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              // "Scrolled past" == the trigger has left the viewport upwards.
              // boundingClientRect.top < 0 distinguishes above from below,
              // which isIntersecting alone cannot do.
              var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              self.setShown(scrolledPast);
            });
          },
          { root: null, rootMargin: '0px', threshold: 0 }
        );
        this.observer.observe(trigger);
        return;
      }

      // Fallback when the trigger is absent (buy_buttons block disabled) or the
      // browser has no IntersectionObserver: a plain scroll threshold.
      var fallbackSelf = this;
      this.scrollHandler = saSafe(function () {
        var target = fallbackSelf.getTriggerElement();
        if (target) {
          fallbackSelf.setShown(target.getBoundingClientRect().bottom < 0);
        } else {
          fallbackSelf.setShown(window.pageYOffset > window.innerHeight);
        }
      });
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.scrollHandler();
    }

    /* ---------------------------------------------------------------- *
     * --sticky-atc-offset
     * ---------------------------------------------------------------- */

    setupResize() {
      var self = this;
      this.resizeHandler = saDebounce(
        saSafe(function () {
          self.updateFloatingOffset();
        }),
        150
      );
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }

    updateFloatingOffset() {
      // offsetHeight is the layout height and is unaffected by the transform,
      // so it is correct whether the bar is currently revealed or not.
      this.writeFloatingOffset(this.shown ? this.offsetHeight : 0);
    }

    writeFloatingOffset(pixels) {
      // `.floating-btn` has no CSS rule anywhere in the theme; it exists purely
      // as this hook. Writing by class covers all three consumers at once.
      var widgets = document.querySelectorAll('.floating-btn');
      for (var i = 0; i < widgets.length; i++) {
        if (pixels > 0) {
          widgets[i].style.setProperty('--sticky-atc-offset', pixels + 'px');
        } else {
          widgets[i].style.setProperty('--sticky-atc-offset', '0px');
        }
      }
    }

    /* ---------------------------------------------------------------- *
     * Scroll mode
     * ---------------------------------------------------------------- */

    setupScrollButton() {
      // Bound once for the lifetime of the node, not once per connect.
      if (this.scrollButtonBound) return;
      if (this.getAttribute('data-scroll-btn') !== 'true') return;
      this.scrollButtonBound = true;

      var button = this.querySelector('.sticky-atc__scroll-btn'); // :681
      if (!button) return;

      var self = this;
      button.addEventListener(
        'click',
        saSafe(function (event) {
          event.preventDefault();
          self.scrollToDestination();
        })
      );
    }

    /**
     * The schema stores the destination as `scroll_<selector>` and the Liquid
     * strips the prefix (`| remove: 'scroll_'`, :462). Three of the four values
     * are id selectors ending in the literal `-id`, which stands in for the
     * section id:
     *
     *   #ProductInfo-id     -> sections/main-product.liquid:114
     *   #variant-selects-id -> snippets/product-variant-picker.liquid:22
     *   #quantity-breaks-id -> snippets/quantity-breaks.liquid:40
     *   .pricing-table      -> plain class, no substitution
     */
    resolveDestination() {
      var raw = this.dataset.scrollDestination;
      if (!raw) return null;

      var selector = raw;
      if (selector.charAt(0) === '#' && /-id$/.test(selector) && this.sectionId) {
        selector = selector.slice(0, -2) + this.sectionId;
      }

      // getElementById avoids having to escape a section id inside a CSS
      // selector, and matches the duplicate-id-tolerant "first in document"
      // behaviour the ids already have.
      if (selector.charAt(0) === '#') return document.getElementById(selector.slice(1));

      try {
        return document.querySelector(selector);
      } catch (error) {
        return null;
      }
    }

    scrollToDestination() {
      var target = this.resolveDestination();
      if (!target) return;

      // Leave room for the sticky header if the merchant enabled one
      // (sections/header.liquid:324 renders <sticky-header> only in that case).
      var header = document.querySelector('sticky-header');
      var headerOffset = header ? header.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20;

      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /* ---------------------------------------------------------------- *
     * Theme editor
     * ---------------------------------------------------------------- */

    /**
     * assets/theme-editor.js has no handling for this block, so the editor
     * affordance lives here. Guard style matches the existing convention in
     * assets/secondary.js (InstaStories).
     */
    setupThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      // These are document-level, so they must be bound once per node.
      if (this.editorBound) return;
      this.editorBound = true;

      var self = this;

      document.addEventListener(
        'shopify:block:select',
        saSafe(function (event) {
          // The host carries {{ block.shopify_attributes }} (:464), so it IS
          // the selected block element.
          if (event.target !== self) return;
          self.editorForcedOpen = true;
          self.setShown(true, true);
        })
      );

      document.addEventListener(
        'shopify:block:deselect',
        saSafe(function (event) {
          if (event.target !== self) return;
          self.editorForcedOpen = false;
          if (self.afterScroll) self.setShown(false, true);
        })
      );

      document.addEventListener(
        'shopify:section:load',
        saSafe(function () {
          // A reloaded section re-measures at a different height.
          self.updateFloatingOffset();
        })
      );
    }
  };

  /* ================================================================== *
   * <clickable-discount>
   * ================================================================== *
   *
   * sections/main-product.liquid:193-237. The whole visual contract is one
   * rule in assets/base.css:
   *
   *   clickable-discount[data-applied="true"]  .clickable-discount__btn__text-1,
   *   clickable-discount[data-applied="false"] .clickable-discount__btn__text-2,
   *   clickable-discount[data-loading="false"] .spinner,
   *   clickable-discount[data-error="false"] + .clickable-discount__error
   *     { display: none; }
   *
   * So this element drives exactly three attributes on itself and touches
   * nothing else. In particular the error <p> (:238) is the element's next
   * sibling and is revealed purely by `data-error="true"` — never re-parent the
   * host or move that paragraph.
   */
  var ClickableDiscount = class ClickableDiscountElement extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      try {
        this.code = (this.dataset.code || '').trim();
        this.button = this.querySelector('.clickable-discount__btn'); // :194

        if (this.button) {
          var self = this;
          this.button.addEventListener(
            'click',
            saSafe(function (event) {
              event.preventDefault();
              self.apply();
            })
          );
        }

        // "If the customer applied the code and went back to the page, the code
        // will stay applied." — schema copy, sections/main-product.liquid:2176.
        this.restoreAppliedState();
      } catch (error) {
        console.error('[clickable-discount] init failed', error);
      }
    }

    /* ---------------------------------------------------------------- *
     * State (the three data-attributes and nothing else)
     * ---------------------------------------------------------------- */

    setLoading(value) {
      this.setAttribute('data-loading', value ? 'true' : 'false');
    }

    setApplied(value) {
      this.setAttribute('data-applied', value ? 'true' : 'false');
    }

    setError(value) {
      this.setAttribute('data-error', value ? 'true' : 'false');
    }

    /* ---------------------------------------------------------------- *
     * Applying
     * ---------------------------------------------------------------- */

    discountUrl() {
      // The Liquid supplies no route attribute, so the URL is built from
      // data-code (:193). /discount/<CODE> is Shopify's own storefront route:
      // it stores the code and carries it into checkout, which is exactly what
      // the schema promises at :2164.
      var root = saShopRoot();
      var redirect = root + 'cart.js';
      return (
        root +
        'discount/' +
        encodeURIComponent(this.code) +
        '?redirect=' +
        encodeURIComponent(redirect)
      );
    }

    apply() {
      if (this.loading) return;
      // Already applied: clicking again is a no-op rather than a re-request.
      if (this.getAttribute('data-applied') === 'true') return;
      if (!this.code) {
        this.setError(true);
        return;
      }

      this.loading = true;
      this.setError(false);
      this.setLoading(true);

      var self = this;

      fetch(this.discountUrl(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json, text/javascript, text/html, */*' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Discount request failed: ' + response.status);
          // The ?redirect= above lands on cart.js, so this is normally JSON.
          // It is only ever used to refine the outcome, never to gate it.
          return response.text().then(function (body) {
            try {
              return JSON.parse(body);
            } catch (error) {
              return null;
            }
          });
        })
        .then(function (cart) {
          self.loading = false;
          self.setLoading(false);

          if (self.cartRejectedCode(cart)) {
            self.setApplied(false);
            self.setError(true);
            return;
          }

          self.setApplied(true);
          self.setError(false);
          self.writeMarker();
        })
        .catch(function (error) {
          console.error('[clickable-discount]', error);
          self.loading = false;
          self.setLoading(false);
          self.setApplied(false);
          self.setError(true);
        });
    }

    /**
     * Returns true only when we can positively prove the code was refused.
     *
     * A cart with items whose `discount_codes` does not mention our code means
     * the code is invalid. An empty cart legitimately reports `discount_codes: []`
     * even for a good code (it is held for checkout), so that case is not an
     * error. Anything unparseable is treated as success.
     */
    cartRejectedCode(cart) {
      if (!cart || typeof cart !== 'object') return false;
      if (!Array.isArray(cart.discount_codes)) return false;
      if (!cart.item_count) return false;
      return !this.matchesCode(cart.discount_codes);
    }

    matchesCode(discountCodes) {
      if (!Array.isArray(discountCodes)) return false;
      var wanted = this.code.toLowerCase();
      for (var i = 0; i < discountCodes.length; i++) {
        var entry = discountCodes[i];
        var code = entry && typeof entry === 'object' ? entry.code : entry;
        if (typeof code === 'string' && code.toLowerCase() === wanted) return true;
      }
      return false;
    }

    /* ---------------------------------------------------------------- *
     * Persistence across page loads
     * ---------------------------------------------------------------- */

    markerKey() {
      return 'sticky-atc:clickable-discount:' + this.code.toLowerCase();
    }

    writeMarker() {
      try {
        localStorage.setItem(this.markerKey(), '1');
      } catch (error) {
        /* Private browsing / storage disabled — the cart check still covers us. */
      }
    }

    readMarker() {
      try {
        return localStorage.getItem(this.markerKey()) === '1';
      } catch (error) {
        return false;
      }
    }

    clearMarker() {
      try {
        localStorage.removeItem(this.markerKey());
      } catch (error) {
        /* no-op */
      }
    }

    /**
     * Two-stage restore:
     *   1. Synchronous local marker so the applied state paints immediately.
     *   2. One idle /cart.js read as the authoritative check — it can promote
     *      to applied (code applied on another page) or demote (cart has items
     *      but the code is gone, e.g. the merchant deactivated it).
     */
    restoreAppliedState() {
      if (!this.code) return;

      if (this.readMarker()) this.setApplied(true);

      var self = this;
      var run = function () {
        fetch(saShopRoot() + 'cart.js', {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('cart.js ' + response.status);
            return response.json();
          })
          .then(function (cart) {
            if (!cart || !Array.isArray(cart.discount_codes)) return;

            if (self.matchesCode(cart.discount_codes)) {
              self.setApplied(true);
              self.writeMarker();
            } else if (cart.item_count) {
              // The cart is populated and Shopify is not reporting our code,
              // so the stored marker is stale.
              self.setApplied(false);
              self.clearMarker();
            }
          })
          .catch(function () {
            /* Offline or blocked: keep whatever the marker decided. */
          });
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(saSafe(run), { timeout: 2000 });
      } else {
        setTimeout(saSafe(run), 500);
      }
    }
  };

  /* ================================================================== *
   * Registration
   * ================================================================== */

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyAtc);
  }

  if (!customElements.get('clickable-discount')) {
    customElements.define('clickable-discount', ClickableDiscount);
  }
})();
